import * as React from 'react';
import MuiChip, { type ChipProps as MuiChipProps } from '@mui/material/Chip';
import MuiAvatar from '@mui/material/Avatar';

export interface ChipProps extends Omit<MuiChipProps, 'onDelete' | 'children'> {
  /** react-toolbox: whether chip is deletable */
  deletable?: boolean;
  /** react-toolbox: callback when delete is clicked */
  onDeleteClick?: () => void;
  /** react-toolbox: children (avatar and label content) */
  children?: React.ReactNode;
}

/**
 * Chip wrapper - maps react-toolbox Chip to MUI Chip
 * - `deletable` + `onDeleteClick` map to `onDelete`
 * - If children contains an Avatar, extract it to the `avatar` prop
 */
const Chip = React.forwardRef<HTMLDivElement, ChipProps>(
  ({ deletable, onDeleteClick, children, avatar: avatarProp, label: labelProp, ...rest }, ref) => {
    // Extract Avatar from children if present
    let avatar = avatarProp;
    let label = labelProp;
    const otherChildren: React.ReactNode[] = [];

    React.Children.forEach(children, (child) => {
      if (React.isValidElement(child)) {
        // Check if it's an Avatar (either MUI or our wrapper)
        const childType = child.type;
        const isAvatar =
          childType === MuiAvatar ||
          (typeof childType === 'function' &&
            ((childType as { displayName?: string }).displayName === 'Avatar' ||
              (childType as { name?: string }).name === 'Avatar'));
        if (isAvatar) {
          if (!avatar) {
            avatar = child as React.ReactElement;
          } else {
            otherChildren.push(child);
          }
        } else {
          otherChildren.push(child);
        }
      } else if (child != null) {
        otherChildren.push(child);
      }
    });

    // Use remaining children as label if no explicit label
    if (!label && otherChildren.length > 0) {
      label = otherChildren;
    }

    // Handle delete
    const onDelete = deletable && onDeleteClick ? onDeleteClick : undefined;

    return <MuiChip ref={ref} avatar={avatar} label={label} onDelete={onDelete} {...rest} />;
  },
);

Chip.displayName = 'Chip';

export default Chip;
